import React, { useState, useRef, useEffect } from "react";
import { useClickOutsideModalListener } from "src/hooks/useClickOutsideModalListener";
import { PrimaryButton, GhostButton } from "src/components/Buttons";
import Input from "src/components/Input";
import { CloseIcon, ChevronPaginationDownIcon } from "src/assets/icons";
import type { Attachment } from "src/types/conditionsInbox";
import {
  ModalWrapper,
  Modal,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FileViewerSection,
  RenameSection,
  SectionTitle,
  DropdownWrapper,
  DropdownSelect,
  DropdownIcon,
  HelpText,
  ButtonGroup,
  FileHeader,
  FileName,
  FilePreview,
  PdfControls,
  ZoomControls,
  FileNameInputWrapper,
  FileNameLabel,
} from "./AttachmentModal.styles";
import FilePreviewContent from "./FilePreviewContent";

interface AttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachment: Attachment;
  onRename: (newName: string) => void;
  fetchAttachmentSignedUrl?: (attachmentId: string) => Promise<string>;
}

const documentTypes = [
  { value: "", label: "What type of document is this?" },
  { value: "Paycheck stub", label: "Paycheck stub" },
  { value: "W2", label: "W2" },
  { value: "Proof of address", label: "Proof of address" },
  { value: "Bank statement", label: "Bank statement" },
  { value: "Tax return", label: "Tax return" },
  { value: "Employment verification", label: "Employment verification" },
  { value: "---", label: "---" },
];

export const AttachmentModal: React.FC<AttachmentModalProps> = ({
  isOpen,
  onClose,
  attachment,
  onRename,
  fetchAttachmentSignedUrl,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [customFileName, setCustomFileName] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>(
    attachment.url,
  );
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  useClickOutsideModalListener(isOpen, onClose, modalRef);

  useEffect(() => {
    if (isOpen && fetchAttachmentSignedUrl && !attachment.url) {
      setIsLoadingUrl(true);
      fetchAttachmentSignedUrl(attachment.id)
        .then((url) => {
          setAttachmentUrl(url);
          setIsLoadingUrl(false);
        })
        .catch((err) => {
          console.error("Failed to fetch attachment URL:", err);
          setIsLoadingUrl(false);
        });
    } else if (attachment.url) {
      setAttachmentUrl(attachment.url);
    }
  }, [isOpen, attachment.id, attachment.url, fetchAttachmentSignedUrl]);

  if (!isOpen) return null;

  const getOriginalFileExtension = (filename: string) => {
    const lastDot = filename.lastIndexOf(".");
    return lastDot !== -1 ? filename.substring(lastDot + 1) : "";
  };

  const handleDocTypeChange = (docType: string) => {
    setSelectedDocType(docType);
    if (docType && docType !== "---" && docType !== "") {
      const extension = getOriginalFileExtension(attachment.name);
      const generatedName = `${docType.replace(/\s+/g, "_")}.${extension}`;
      setCustomFileName(generatedName);
    }
  };

  const handleDone = () => {
    if (customFileName.trim()) {
      const trimmedName = customFileName.trim();
      const originalExtension = getOriginalFileExtension(attachment.name);

      const finalName = trimmedName.includes(".")
        ? trimmedName
        : `${trimmedName}.${originalExtension}`;

      onRename(finalName);
    }
    onClose();
  };

  const isPdf = attachment.type.toLowerCase() === "pdf";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
    attachment.type.toLowerCase(),
  );

  const [imageLoadError, setImageLoadError] = useState(false);

  return (
    <ModalWrapper>
      <Modal ref={modalRef}>
        <ModalHeader>
          <ModalTitle>Rename File</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <FileViewerSection>
            <FileHeader>
              <FileName>{attachment.name}</FileName>
            </FileHeader>

            <FilePreview>
              <FilePreviewContent
                isLoading={isLoadingUrl}
                isPdf={isPdf}
                isImage={isImage}
                url={attachmentUrl}
                fileName={attachment.name}
                imageLoadError={imageLoadError}
                onImageError={() => setImageLoadError(true)}
              />
            </FilePreview>

            {isPdf && (
              <PdfControls>
                <span>Page 1 / 1</span>
                <ZoomControls>
                  <button>−</button>
                  <button>+</button>
                </ZoomControls>
              </PdfControls>
            )}
          </FileViewerSection>

          <RenameSection>
            <SectionTitle>Rename file</SectionTitle>

            <DropdownWrapper>
              <DropdownSelect
                value={selectedDocType}
                onChange={(e) => handleDocTypeChange(e.target.value)}
              >
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon>
                <ChevronPaginationDownIcon />
              </DropdownIcon>
            </DropdownWrapper>

            <HelpText>
              Select a document type to auto-generate a filename, or create your
              own custom name below.
            </HelpText>

            <FileNameInputWrapper>
              <FileNameLabel>File Name:</FileNameLabel>
              <Input
                id="custom-filename"
                name="filename"
                value={customFileName}
                onChange={(e) => setCustomFileName(e.target.value)}
                placeholder="Enter custom filename..."
                showError={false}
                onFocus={() => {}}
                onBlur={() => {}}
              />
            </FileNameInputWrapper>

            <ButtonGroup>
              <PrimaryButton
                onClick={handleDone}
                disabled={!customFileName.trim()}
              >
                Done
              </PrimaryButton>
              <GhostButton onClick={onClose}>Cancel</GhostButton>
            </ButtonGroup>
          </RenameSection>
        </ModalContent>
      </Modal>
    </ModalWrapper>
  );
};
